import * as React from 'react';
import { MISAFIR_TALEPLERI, talepMetni, type MisafirTalepTipi } from '@hotelcms/shared';
import { Button } from '@/components/ui/button';
import { Kart, KartBaslik, KartGovde } from '@/components/ui/card';
import { ApiHatasi, api } from '@/lib/api';
import { paraFormatla } from '@/lib/utils';

interface TalepOzet {
  tipler: {
    tip: string;
    adet: number;
    tamamlanan: number;
    iptal: number;
    ortalamaPuan: number | null;
    ortalamaDakika: number | null;
    gelirKurus: number;
  }[];
  toplam: number;
  ortalamaPuan: number | null;
  puanAdedi: number;
  gelirKurus: number;
}

/**
 * Raporlar ekrani.
 *
 * Tasarim kaynagi: Claude Design projesi — "otelcms Raporlar.dc.html".
 * Tasarimdaki veriler ureticiydi (sentetik); buradaki her sayi gercek
 * uclardan gelir:
 *   /api/raporlar/ozet                -> gunluk doluluk / ADR / RevPAR
 *   /api/raporlar/kaynak-dagilimi     -> rezervasyon kaynagi
 *   /api/raporlar/kasa                -> tahsilat kirilimi   (report:financial)
 *   /api/raporlar/konaklama-vergisi   -> vergi tahakkuku     (report:financial)
 *
 * Mali bolumler yetkisiz kullanicida (ornegin resepsiyon) GIZLENIR; sunucu
 * zaten 403 doner, arayuz de bos kutu gostermez.
 */

interface GunKaydi {
  tarih: string;
  dolulukYuzde: number;
  adrKurus: number;
  revparKurus: number;
  satilanOda: number;
}
interface OzetCevap {
  gunler: GunKaydi[];
  ortalama: { dolulukYuzde: number; adrKurus: number; revparKurus: number; satilanOdaToplam: number };
}
interface KaynakCevap {
  dagilim: { kaynak: string; adet: number; toplamKurus: string | number | null }[];
}
interface KasaCevap {
  tahsilat: { tip: string; paraBirimi: string; adet: number; toplamKurus: string | number | null }[];
}
interface VergiCevap {
  toplamKurus: number;
  gunler: { tarih: string; adet: number; toplamKurus: string | number | null }[];
}

const ARALIKLAR = [7, 14, 30] as const;
type Aralik = (typeof ARALIKLAR)[number];

const KAYNAK_ADI: Record<string, string> = {
  DIREKT: 'Direkt',
  TELEFON: 'Telefon',
  WALK_IN: 'Walk-in',
  ACENTA: 'Acenta',
  WEB: 'Web',
  KANAL: 'Kanal (OTA)',
  GRUP: 'Grup',
};
const KAYNAK_RENK = ['#1f4fd8', '#0b1530', '#8fb0ff', '#f2b632', '#12a150', '#e5484d', '#94a3b8'];

const TAHSILAT_ADI: Record<string, string> = {
  NAKIT: 'Nakit',
  KREDI_KARTI: 'Kredi kartı',
  HAVALE: 'Havale / EFT',
  ONLINE: 'Online ödeme',
  ACENTA_CARI: 'Acenta cari',
  DEPOZITO: 'Depozito',
};
const TAHSILAT_RENK: Record<string, string> = {
  NAKIT: '#12a150',
  KREDI_KARTI: '#1f4fd8',
  HAVALE: '#0b1530',
  ONLINE: '#8fb0ff',
  ACENTA_CARI: '#e5484d',
  DEPOZITO: '#f2b632',
};

const sayi = (v: string | number | null | undefined) => Number(v ?? 0);
const para = (kurus: number) => `₺${Math.round(kurus / 100).toLocaleString('tr-TR')}`;
const gunEkle = (gun: number) => {
  const d = new Date();
  d.setDate(d.getDate() + gun);
  return d.toISOString().slice(0, 10);
};
const kisaTarih = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
const haftaSonuMu = (iso: string) => {
  const g = new Date(`${iso}T00:00:00`).getDay();
  return g === 0 || g === 6;
};

/**
 * Misafir mobil talepleri ozeti.
 *
 * Misafirin verdigi yildizin OKUNDUGU tek yer burasi. Tarih araligindan
 * bagimsizdir: konaklama boyu birikir ve saklama suresi dolunca KVKK
 * temizligiyle silinir (bkz. cron/belge-temizlik.ts).
 */
function MisafirTalepOzeti() {
  const [veri, setVeri] = React.useState<TalepOzet | null>(null);

  React.useEffect(() => {
    void api<TalepOzet>('/api/misafir-talepleri/ozet')
      .then(setVeri)
      .catch(() => setVeri(null));
  }, []);

  // Hic talep gelmemisse bos bir kart gostermek yerine bolumu hic acmayiz.
  if (!veri || veri.toplam === 0) return null;

  return (
    <Kart className="[animation:rise_.6s_.25s_cubic-bezier(.2,.7,.2,1)_both]">
      <KartBaslik>
        <h2 className="text-[17px] font-bold tracking-[-.01em]">Misafir talepleri</h2>
        <div className="ml-auto flex items-center gap-4 text-[13px] text-gri">
          <span>
            <strong className="text-lacivert">{veri.toplam}</strong> talep
          </span>
          {veri.ortalamaPuan !== null && (
            <span className="flex items-center gap-1">
              <span className="text-sari">★</span>
              <strong className="text-lacivert">{veri.ortalamaPuan}</strong>
              <span>({veri.puanAdedi} puan)</span>
            </span>
          )}
          {veri.gelirKurus > 0 && (
            <span>
              Folyoya işlenen <strong className="text-lacivert">{paraFormatla(veri.gelirKurus)}</strong>
            </span>
          )}
        </div>
      </KartBaslik>
      <KartGovde className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-slate-600">
            <tr>
              <th className="p-3">Talep</th>
              <th className="p-3 text-right">Adet</th>
              <th className="p-3 text-right">Tamamlanan</th>
              <th className="p-3 text-right">İptal</th>
              <th className="p-3 text-right">Ort. süre</th>
              <th className="p-3 text-right">Puan</th>
              <th className="p-3 text-right">Folyo</th>
            </tr>
          </thead>
          <tbody>
            {veri.tipler.map((t) => (
              <tr key={t.tip} className="border-t border-slate-100">
                <td className="p-3 font-semibold text-lacivert">
                  {MISAFIR_TALEPLERI[t.tip as MisafirTalepTipi]
                    ? talepMetni(t.tip as MisafirTalepTipi).baslik
                    : t.tip}
                </td>
                <td className="p-3 text-right tabular-nums">{t.adet}</td>
                <td className="p-3 text-right tabular-nums text-yesil">{t.tamamlanan}</td>
                <td className="p-3 text-right tabular-nums text-gri">{t.iptal || '—'}</td>
                <td className="p-3 text-right tabular-nums">
                  {t.ortalamaDakika !== null ? `${t.ortalamaDakika} dk` : '—'}
                </td>
                <td className="p-3 text-right tabular-nums">
                  {t.ortalamaPuan !== null ? (
                    <span>
                      <span className="text-sari">★</span> {t.ortalamaPuan}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="p-3 text-right tabular-nums">
                  {t.gelirKurus > 0 ? paraFormatla(t.gelirKurus) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </KartGovde>
    </Kart>
  );
}

export function Raporlar({ yetkiler }: { yetkiler: string[] }) {
  const maliYetki = yetkiler.includes('report:financial');

  const [aralik, setAralik] = React.useState<Aralik>(14);
  const [ozet, setOzet] = React.useState<OzetCevap | null>(null);
  const [onceki, setOnceki] = React.useState<OzetCevap | null>(null);
  const [kaynak, setKaynak] = React.useState<KaynakCevap | null>(null);
  const [kasa, setKasa] = React.useState<KasaCevap | null>(null);
  const [vergi, setVergi] = React.useState<VergiCevap | null>(null);
  const [yukleniyor, setYukleniyor] = React.useState(true);
  const [hata, setHata] = React.useState<string | null>(null);

  const yukle = React.useCallback(async () => {
    setYukleniyor(true);
    setHata(null);
    const bitis = gunEkle(0);
    const baslangic = gunEkle(-(aralik - 1));
    const oncekiBitis = gunEkle(-aralik);
    const oncekiBaslangic = gunEkle(-(aralik * 2 - 1));
    const q = (b: string, s: string) => `baslangic=${b}&bitis=${s}`;

    try {
      const [o, p, k] = await Promise.all([
        api<OzetCevap>(`/api/raporlar/ozet?${q(baslangic, bitis)}`),
        api<OzetCevap>(`/api/raporlar/ozet?${q(oncekiBaslangic, oncekiBitis)}`).catch(() => null),
        api<KaynakCevap>(`/api/raporlar/kaynak-dagilimi?${q(baslangic, bitis)}`).catch(() => null),
      ]);
      setOzet(o);
      setOnceki(p);
      setKaynak(k);

      if (maliYetki) {
        const [ks, vr] = await Promise.all([
          api<KasaCevap>(`/api/raporlar/kasa?${q(baslangic, bitis)}`).catch(() => null),
          api<VergiCevap>(`/api/raporlar/konaklama-vergisi?${q(baslangic, bitis)}`).catch(() => null),
        ]);
        setKasa(ks);
        setVergi(vr);
      }
    } catch (e) {
      setHata(e instanceof ApiHatasi ? e.message : 'Raporlar alınamadı.');
    } finally {
      setYukleniyor(false);
    }
  }, [aralik, maliYetki]);

  React.useEffect(() => {
    void yukle();
  }, [yukle]);

  const gunler = ozet?.gunler ?? [];
  const veriVar = gunler.length > 0;

  // Gelir = gunluk ADR x satilan oda
  const gelir = gunler.reduce((t, g) => t + g.adrKurus * g.satilanOda, 0);
  const oncekiGelir = (onceki?.gunler ?? []).reduce((t, g) => t + g.adrKurus * g.satilanOda, 0);

  /**
   * Onceki donemle karsilastirma.
   *
   * Gece kapanisi kayitlari geriye dogru sinirli oldugu icin onceki pencere
   * eksik olabilir. Ornegin 14 gunluk toplam geliri, yalnizca 1 gunluk veri
   * bulunan onceki pencereyle kiyaslamak "+%1029" gibi anlamsiz bir fark
   * uretir. Bu yuzden pencereler karsilastirilabilir degilse fark gosterilmez.
   */
  const oncekiGunSayisi = onceki?.gunler.length ?? 0;
  const karsilastirilabilir = oncekiGunSayisi >= Math.ceil(gunler.length * 0.8) && oncekiGunSayisi > 0;
  const fark = (simdi: number, evvel: number) =>
    karsilastirilabilir && evvel > 0 ? Math.round(((simdi - evvel) / evvel) * 100) : null;

  const kpiler = [
    {
      etiket: 'Doluluk',
      deger: veriVar ? `%${Math.round(ozet!.ortalama.dolulukYuzde)}` : '—',
      delta: fark(ozet?.ortalama.dolulukYuzde ?? 0, onceki?.ortalama.dolulukYuzde ?? 0),
      seri: gunler.map((g) => g.dolulukYuzde),
      koyu: true,
    },
    {
      etiket: 'ADR',
      deger: veriVar ? para(ozet!.ortalama.adrKurus) : '—',
      delta: fark(ozet?.ortalama.adrKurus ?? 0, onceki?.ortalama.adrKurus ?? 0),
      seri: gunler.map((g) => g.adrKurus),
      koyu: false,
    },
    {
      etiket: 'RevPAR',
      deger: veriVar ? para(ozet!.ortalama.revparKurus) : '—',
      delta: fark(ozet?.ortalama.revparKurus ?? 0, onceki?.ortalama.revparKurus ?? 0),
      seri: gunler.map((g) => g.revparKurus),
      koyu: false,
    },
    {
      etiket: 'Gelir',
      deger: veriVar ? para(gelir) : '—',
      delta: fark(gelir, oncekiGelir),
      seri: gunler.map((g) => g.adrKurus * g.satilanOda),
      koyu: false,
    },
  ];

  function excelIndir() {
    const basliklar = ['tarih', 'doluluk_yuzde', 'adr_tl', 'revpar_tl', 'satilan_oda', 'gelir_tl'];
    const satirlar = gunler.map((g) =>
      [
        g.tarih,
        g.dolulukYuzde,
        (g.adrKurus / 100).toFixed(2),
        (g.revparKurus / 100).toFixed(2),
        g.satilanOda,
        ((g.adrKurus * g.satilanOda) / 100).toFixed(2),
      ].join(';'),
    );
    // BOM + noktali virgul: Excel'in Turkce yerelde dogru acmasi icin
    const icerik = `﻿${basliklar.join(';')}\n${satirlar.join('\n')}\n`;
    const url = URL.createObjectURL(new Blob([icerik], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `otelcms-rapor-${gunEkle(-(aralik - 1))}_${gunEkle(0)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <main className="flex flex-1 flex-col gap-[22px] bg-zemin p-5 sm:p-7">
      {/* Baslik + aralik secimi */}
      <div className="flex flex-wrap items-center gap-3.5 [animation:rise_.5s_cubic-bezier(.2,.7,.2,1)_both]">
        <h1 className="text-3xl font-bold tracking-[-.03em]">Raporlar</h1>
        <div className="flex w-full gap-1 rounded-full border border-cizgi bg-white p-1 sm:w-auto overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {ARALIKLAR.map((a) => (
            <button
              key={a}
              onClick={() => setAralik(a)}
              className={
                'cursor-pointer whitespace-nowrap rounded-full px-4 py-[7px] text-[13px] font-semibold transition-colors ' +
                (aralik === a ? 'bg-lacivert text-white' : 'text-gri hover:text-lacivert')
              }
            >
              {a} gün
            </button>
          ))}
        </div>
        {veriVar && (
          <span className="flex items-center gap-2 rounded-full bg-yesil-zemin px-3 py-1.5 text-[13px] font-semibold text-yesil">
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="7" cy="7" r="6" />
              <path d="M4.5 7l2 2 3-3.5" />
            </svg>
            Gece kapanışı 04:00 · {gunler.length} gün işlendi
          </span>
        )}
        <button
          onClick={excelIndir}
          disabled={!veriVar}
          className="ml-auto flex cursor-pointer items-center gap-2 rounded-full border-[1.5px] border-lacivert px-[18px] py-2.5 text-sm font-semibold text-lacivert transition-colors hover:bg-lacivert hover:text-white disabled:opacity-40"
        >
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 2v9M4.5 7.5L8 11l3.5-3.5M2 13h12" />
          </svg>
          Excel indir
        </button>
      </div>

      {hata && <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-red-800">{hata}</div>}

      {yukleniyor && !ozet ? (
        <Iskelet />
      ) : !veriVar ? (
        <BosDurum />
      ) : (
        <>
          {/* KPI kartlari */}
          <div className="grid gap-3.5 [animation:rise_.6s_.05s_cubic-bezier(.2,.7,.2,1)_both] sm:grid-cols-2 xl:grid-cols-4">
            {kpiler.map((k) => (
              <KpiKart key={k.etiket} {...k} />
            ))}
          </div>

          <div className="grid gap-[18px] xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
            <DolulukGrafigi gunler={gunler} ortalama={ozet!.ortalama.dolulukYuzde} />
            <AdrGrafigi gunler={gunler} />
          </div>

          <div className="grid items-start gap-[18px] xl:grid-cols-2">
            {maliYetki ? <KasaOzeti kasa={kasa} aralik={aralik} /> : <MaliYetkiYok />}
            <div className="flex flex-col gap-[18px]">
              <KaynakDagilimi kaynak={kaynak} />
              {maliYetki && vergi && <VergiBanti toplamKurus={vergi.toplamKurus} />}
            </div>
          </div>

          <MisafirTalepOzeti />
        </>
      )}
    </main>
  );
}

/* ------------------------------------------------------------------ KPI */

function sparkPath(seri: number[]): string {
  if (seri.length < 2) return '';
  const mn = Math.min(...seri);
  const mx = Math.max(...seri);
  return seri
    .map((v, i) => {
      const x = (i / (seri.length - 1)) * 100;
      const y = 22 - ((v - mn) / (mx - mn || 1)) * 20;
      return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

function KpiKart({
  etiket,
  deger,
  delta,
  seri,
  koyu,
}: {
  etiket: string;
  deger: string;
  delta: number | null;
  seri: number[];
  koyu: boolean;
}) {
  const artis = (delta ?? 0) >= 0;
  return (
    <div
      className={
        'relative flex flex-col gap-2.5 overflow-hidden rounded-[18px] border p-5 ' +
        (koyu ? 'border-lacivert bg-lacivert text-white' : 'border-cizgi bg-white text-lacivert')
      }
    >
      <span className="text-xs font-semibold uppercase tracking-[.08em] opacity-70">{etiket}</span>
      <span className="text-[34px] font-bold leading-none tracking-[-.03em] tabular-nums">{deger}</span>
      <div className="flex items-center gap-2 text-[13px]">
        {delta === null ? (
          <span className="opacity-60">karşılaştırılacak önceki dönem verisi yok</span>
        ) : (
          <>
            <span
              className="flex items-center gap-[3px] font-bold"
              style={{ color: artis ? (koyu ? '#5fd48f' : '#12a150') : '#e5484d' }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ transform: artis ? 'none' : 'rotate(180deg)' }}
              >
                <path d="M5 9V1M1.5 4.5L5 1l3.5 3.5" />
              </svg>
              {artis ? '+' : ''}
              {delta}%
            </span>
            <span className="opacity-60">geçen döneme göre</span>
          </>
        )}
      </div>
      <svg viewBox="0 0 100 24" width="100%" height="24" fill="none" className="mt-1">
        <path
          d={sparkPath(seri)}
          stroke={koyu ? '#8fb0ff' : '#1f4fd8'}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="200"
          strokeDashoffset="200"
          style={{ animation: 'draw 1.4s .4s cubic-bezier(.2,.8,.2,1) forwards' }}
        />
      </svg>
    </div>
  );
}

/* -------------------------------------------------------------- doluluk */

function DolulukGrafigi({ gunler, ortalama }: { gunler: GunKaydi[]; ortalama: number }) {
  // 30 gunde her gunu etiketlemek okunmaz oluyor; tasarimdaki gibi seyreltiyoruz
  const adim = gunler.length <= 14 ? 1 : Math.ceil(gunler.length / 14);
  const gosterilen = gunler.filter((_, i) => i % adim === 0 || i === gunler.length - 1);

  return (
    <div className="flex flex-col gap-[18px] rounded-[20px] border border-cizgi bg-white p-6 shadow-[0_30px_60px_-40px_rgba(11,21,48,.25)] [animation:rise_.6s_.1s_cubic-bezier(.2,.7,.2,1)_both]">
      <div className="flex flex-wrap items-start justify-between gap-2.5">
        <div>
          <h3 className="text-[17px] font-bold tracking-[-.01em]">Doluluk</h3>
          <p className="mt-1 text-[13px] text-gri">
            Ortalama %{Math.round(ortalama)} · hafta sonları koyu
          </p>
        </div>
        <div className="flex gap-3.5 text-[13px] text-gri">
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-[3px] bg-marka-600" />
            Hafta içi
          </span>
          <span className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-[3px] bg-lacivert" />
            Hafta sonu
          </span>
        </div>
      </div>

      <div
        className="grid h-[220px] items-end gap-2 border-b border-cizgi pb-1.5"
        style={{ gridTemplateColumns: `repeat(${gosterilen.length}, minmax(0,1fr))` }}
      >
        {gosterilen.map((g, i) => (
          <div key={g.tarih} title={`%${Math.round(g.dolulukYuzde)} · ${para(g.revparKurus)} RevPAR`} className="flex h-full flex-col justify-end gap-0.5">
            <div className="flex-1 rounded-t-md bg-satir" />
            <div
              className="relative rounded-md"
              style={{
                height: `${Math.max(2, g.dolulukYuzde)}%`,
                background: haftaSonuMu(g.tarih) ? '#0b1530' : '#1f4fd8',
                transformOrigin: 'bottom',
                animation: `growY .8s cubic-bezier(.2,.8,.2,1) both`,
                animationDelay: `${i * 40}ms`,
              }}
            >
              <span
                className="absolute inset-x-0 -top-5 text-center text-[11px] font-bold text-lacivert"
                style={{ animation: 'fadein .4s both', animationDelay: `${i * 40 + 500}ms` }}
              >
                %{Math.round(g.dolulukYuzde)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div
        className="grid gap-2 text-center text-[11px] font-medium"
        style={{ gridTemplateColumns: `repeat(${gosterilen.length}, minmax(0,1fr))` }}
      >
        {gosterilen.map((g) => (
          <span key={g.tarih} style={{ color: haftaSonuMu(g.tarih) ? '#0b1530' : '#5b6480' }}>
            {kisaTarih(g.tarih)}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ ADR/RevPAR */

function AdrGrafigi({ gunler }: { gunler: GunKaydi[] }) {
  const adim = gunler.length <= 14 ? 1 : Math.ceil(gunler.length / 12);
  const g = gunler.filter((_, i) => i % adim === 0 || i === gunler.length - 1);

  // Olcek tasarimda sabitti (₺1.500–₺3.000); gercek veride tasmamasi icin hesaplaniyor
  const tumu = [...g.map((x) => x.adrKurus), ...g.map((x) => x.revparKurus)];
  const ham = { alt: Math.min(...tumu), ust: Math.max(...tumu) };
  const pay = Math.max(1, (ham.ust - ham.alt) * 0.15);
  const alt = Math.max(0, Math.floor((ham.alt - pay) / 10000) * 10000);
  const ust = Math.ceil((ham.ust + pay) / 10000) * 10000;

  const x = (i: number) => 46 + (i / Math.max(1, g.length - 1)) * 344;
  const y = (v: number) => 210 - ((v - alt) / (ust - alt || 1)) * 175;
  const cizgi = (al: (d: GunKaydi) => number) =>
    g.map((d, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(al(d)).toFixed(1)}`).join(' ');

  const adrYol = cizgi((d) => d.adrKurus);
  const revYol = cizgi((d) => d.revparKurus);
  const alan = `${adrYol} L${x(g.length - 1).toFixed(1)} 210 L46 210 Z`;
  const izgara = [0, 1, 2, 3].map((i) => ({ y: 35 + i * 58.33, deger: ust - ((ust - alt) / 3) * i }));

  return (
    <div className="relative flex flex-col gap-[18px] overflow-hidden rounded-[20px] bg-lacivert p-6 text-white [animation:rise_.6s_.15s_cubic-bezier(.2,.7,.2,1)_both]">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.05) 1px,transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      <div className="relative flex items-start justify-between">
        <div>
          <h3 className="text-[17px] font-bold tracking-[-.01em]">ADR &amp; RevPAR</h3>
          <p className="mt-1 text-[13px] text-[#b6c2e2]">Günlük ortalama, ₺</p>
        </div>
        <div className="flex gap-3.5 text-[13px] text-[#b6c2e2]">
          <span className="flex items-center gap-1.5">
            <span className="h-[3px] w-2.5 rounded-sm bg-mavi-acik" />
            ADR
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-[3px] w-2.5 rounded-sm bg-sari" />
            RevPAR
          </span>
        </div>
      </div>

      <svg viewBox="0 0 400 220" width="100%" className="relative flex-1 overflow-visible" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <g stroke="rgba(255,255,255,.12)" strokeWidth="1">
          {izgara.map((c) => (
            <path key={c.y} d={`M40 ${c.y}h360`} />
          ))}
        </g>
        <g fontFamily="Space Grotesk" fontSize="9" fill="#8a93ad">
          {izgara.map((c) => (
            <text key={c.y} x="0" y={c.y + 3}>
              {para(c.deger)}
            </text>
          ))}
        </g>
        <path d={alan} fill="url(#adrFill)" />
        <defs>
          <linearGradient id="adrFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#8fb0ff" stopOpacity=".35" />
            <stop offset="1" stopColor="#8fb0ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={adrYol} stroke="#8fb0ff" strokeWidth="2.5" strokeDasharray="900" strokeDashoffset="900" style={{ animation: 'draw 1.8s .3s cubic-bezier(.2,.8,.2,1) forwards' }} />
        <path d={revYol} stroke="#f2b632" strokeWidth="2.5" strokeDasharray="900" strokeDashoffset="900" style={{ animation: 'draw 1.8s .6s cubic-bezier(.2,.8,.2,1) forwards' }} />
        {g.map((d, i) => (
          <React.Fragment key={d.tarih}>
            <circle cx={x(i)} cy={y(d.adrKurus)} r="4" fill="#0b1530" stroke="#8fb0ff" strokeWidth="2.5" style={{ animation: 'fadein .4s both', animationDelay: `${300 + i * 80}ms` }} />
            <circle cx={x(i)} cy={y(d.revparKurus)} r="4" fill="#0b1530" stroke="#f2b632" strokeWidth="2.5" style={{ animation: 'fadein .4s both', animationDelay: `${600 + i * 80}ms` }} />
          </React.Fragment>
        ))}
        <g style={{ animation: 'fadein .5s 2s both' }}>
          <circle cx={x(g.length - 1)} cy={y(g[g.length - 1]!.adrKurus)} r="6" fill="#8fb0ff" />
          <circle
            cx={x(g.length - 1)}
            cy={y(g[g.length - 1]!.adrKurus)}
            r="6"
            fill="none"
            stroke="#8fb0ff"
            strokeWidth="2"
            style={{ transformBox: 'fill-box', transformOrigin: 'center', animation: 'ring 2s ease-out infinite' }}
          />
        </g>
      </svg>
    </div>
  );
}

/* ----------------------------------------------------------------- kasa */

function KasaOzeti({ kasa, aralik }: { kasa: KasaCevap | null; aralik: number }) {
  const satirlar = (kasa?.tahsilat ?? []).map((t) => ({
    etiket: TAHSILAT_ADI[t.tip] ?? t.tip,
    kurus: sayi(t.toplamKurus),
    adet: t.adet,
    renk: TAHSILAT_RENK[t.tip] ?? '#94a3b8',
  }));
  const toplam = satirlar.reduce((t, s) => t + s.kurus, 0);

  return (
    <div className="overflow-hidden rounded-[20px] border border-cizgi bg-white [animation:rise_.6s_.2s_cubic-bezier(.2,.7,.2,1)_both]">
      <div className="flex flex-wrap items-center justify-between gap-1.5 px-6 pb-3 pt-5">
        <h3 className="whitespace-nowrap text-[17px] font-bold tracking-[-.01em]">Kasa özeti</h3>
        <span className="text-[13px] text-gri">Son {aralik} gün</span>
      </div>

      {satirlar.length === 0 ? (
        <p className="border-t border-satir px-6 py-8 text-center text-sm text-gri">
          Bu dönemde tahsilat kaydı yok.
        </p>
      ) : (
        satirlar.map((s, i) => {
          const oran = toplam > 0 ? Math.round((s.kurus / toplam) * 100) : 0;
          return (
            <div
              key={s.etiket}
              className="grid grid-cols-[1fr_auto] items-center gap-3 border-t border-satir px-6 py-3 [animation:rise_.4s_both]"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex min-w-0 flex-col gap-1.5">
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{s.etiket}</span>
                  <span className="text-[13px] text-gri">
                    %{oran} · {s.adet} işlem
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-[3px] bg-satir">
                  <div
                    className="h-full origin-left"
                    style={{ width: `${oran}%`, background: s.renk, animation: 'bar 1s .4s cubic-bezier(.2,.8,.2,1) both' }}
                  />
                </div>
              </div>
              <span className="min-w-24 text-right text-base font-bold tabular-nums">{para(s.kurus)}</span>
            </div>
          );
        })
      )}

      <div className="flex justify-between border-t-2 border-lacivert bg-[#fbfcfe] px-6 py-4 text-base font-bold">
        <span>Toplam</span>
        <span className="tabular-nums">{para(toplam)}</span>
      </div>
    </div>
  );
}

function MaliYetkiYok() {
  return (
    <div className="flex flex-col items-center gap-2 rounded-[20px] border border-dashed border-cizgi bg-white p-10 text-center">
      <span className="text-2xl">🔒</span>
      <h3 className="text-[17px] font-bold">Kasa ve vergi raporları</h3>
      <p className="max-w-sm text-sm text-gri">
        Bu bölümü görüntülemek için mali rapor yetkisi gerekiyor. Yöneticinize danışın.
      </p>
    </div>
  );
}

/* --------------------------------------------------------------- kaynak */

function KaynakDagilimi({ kaynak }: { kaynak: KaynakCevap | null }) {
  const satirlar = (kaynak?.dagilim ?? []).slice().sort((a, b) => b.adet - a.adet);
  const toplam = satirlar.reduce((t, s) => t + s.adet, 0);
  const C = 2 * Math.PI * 46;
  let birikim = 0;

  const dilimler = satirlar.map((s, i) => {
    const oran = toplam > 0 ? (s.adet / toplam) * 100 : 0;
    const dilim = {
      etiket: KAYNAK_ADI[s.kaynak] ?? s.kaynak,
      oran: Math.round(oran),
      renk: KAYNAK_RENK[i % KAYNAK_RENK.length]!,
      dash: `${((oran / 100) * C).toFixed(1)} ${C.toFixed(1)}`,
      offset: ((-birikim / 100) * C).toFixed(1),
      gecikme: `${200 + i * 150}ms`,
    };
    birikim += oran;
    return dilim;
  });

  return (
    <div className="flex items-center gap-[22px] rounded-[20px] border border-cizgi bg-white p-6 [animation:rise_.6s_.25s_cubic-bezier(.2,.7,.2,1)_both]">
      <svg width="110" height="110" viewBox="0 0 110 110" className="shrink-0 -rotate-90">
        <circle cx="55" cy="55" r="46" fill="none" stroke="#eef1f8" strokeWidth="12" />
        {dilimler.map((d) => (
          <circle
            key={d.etiket}
            cx="55"
            cy="55"
            r="46"
            fill="none"
            stroke={d.renk}
            strokeWidth="12"
            strokeDasharray={d.dash}
            strokeDashoffset={d.offset}
            style={{ animation: 'fadein .6s both', animationDelay: d.gecikme }}
          />
        ))}
      </svg>
      <div className="flex flex-1 flex-col gap-2.5">
        <h3 className="text-[17px] font-bold tracking-[-.01em]">Rezervasyon kaynağı</h3>
        {dilimler.length === 0 ? (
          <p className="text-sm text-gri">Bu dönemde rezervasyon kaydı yok.</p>
        ) : (
          dilimler.map((d) => (
            <div key={d.etiket} className="flex items-center gap-2.5 text-sm">
              <span className="size-2.5 rounded-[3px]" style={{ background: d.renk }} />
              <span className="flex-1">{d.etiket}</span>
              <span className="font-bold tabular-nums">%{d.oran}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function VergiBanti({ toplamKurus }: { toplamKurus: number }) {
  return (
    <div className="flex items-center gap-4 rounded-[20px] bg-mavi-zemin px-6 py-[22px] [animation:rise_.6s_.3s_cubic-bezier(.2,.7,.2,1)_both]">
      <svg width="44" height="44" viewBox="0 0 44 44" fill="none" stroke="#1f4fd8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
        <rect x="6" y="8" width="32" height="30" rx="6" fill="#fff" />
        <path d="M14 8V4m16 4V4M6 16h32M14 24h4m4 0h4m4 0h4M14 31h4m4 0h4" />
      </svg>
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-bold">Konaklama vergisi beyanı</span>
        <span className="text-[13px] text-gri">
          Bu dönem tahakkuk: <strong className="text-lacivert">{para(toplamKurus)}</strong>
        </span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ durumlar */

function Iskelet() {
  return (
    <div className="flex flex-col gap-[22px]">
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[150px] animate-pulse rounded-[18px] border border-cizgi bg-white" />
        ))}
      </div>
      <div className="grid gap-[18px] xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="h-[360px] animate-pulse rounded-[20px] border border-cizgi bg-white" />
        <div className="h-[360px] animate-pulse rounded-[20px] bg-lacivert/10" />
      </div>
    </div>
  );
}

function BosDurum() {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[20px] border border-dashed border-cizgi bg-white p-14 text-center">
      <span className="text-4xl">📊</span>
      <h2 className="text-xl font-bold">Henüz rapor verisi yok</h2>
      <p className="max-w-md text-sm text-gri">
        Raporlar, her gece 04:00'te çalışan gece kapanışının ürettiği kayıtlardan hesaplanır. İlk kapanış
        çalıştıktan sonra burada doluluk, ADR ve RevPAR grafiklerini göreceksiniz.
      </p>
    </div>
  );
}
